// Acciones del PDF del acuse: compartir (hoja nativa → WhatsApp, correo…) y descargar.
//
// Hay DOS implementaciones porque el entorno del repartidor y el de oficina no se
// parecen en nada:
//
// - APP (WebView de Android): `navigator.share` NO existe y un `<a download>` no baja
//   nada, el WebView no trae gestor de descargas. Por eso aquí se usan los plugins
//   nativos: se escribe el PDF en caché y se abre la hoja de compartir del sistema.
//   Es UN SOLO botón a propósito — la hoja de Android ya incluye "Guardar en Archivos",
//   así que un botón de descarga aparte solo pediría permisos de almacenamiento para
//   duplicar algo que el sistema ya hace.
// - NAVEGADOR (PC/PWA): Web Share de archivos si existe, y si no, descarga directa.
import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Button } from '@/components/ui/Button'

interface Props {
  url: string // URL FIRMADA del PDF (misma origin). Nunca el endpoint con Bearer: aquí
  // se baja con fetch() pelón y ese devolvería 401.
  folio: string // p.ej. CE-41
}

const isNative = Capacitor.isNativePlatform()

/** ¿El usuario solo cerró la hoja de compartir? Eso no es un error. */
function isCancel(e: unknown): boolean {
  const err = e as { name?: string; message?: string }
  return err?.name === 'AbortError' || /cancel/i.test(err?.message ?? '')
}

/** Filesystem espera base64 pelón; readAsDataURL trae "data:...;base64," al frente. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onerror = () => reject(new Error('No se pudo leer el PDF.'))
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
    r.readAsDataURL(blob)
  })
}

export function AcuseActions({ url, folio }: Props) {
  const [busy, setBusy] = useState<'share' | 'download' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchBlob = async (): Promise<Blob> => {
    const res = await fetch(url)
    if (!res.ok) throw new Error('No se pudo obtener el PDF.')
    return res.blob()
  }

  const fetchPdf = async (): Promise<File> =>
    new File([await fetchBlob()], `${folio}.pdf`, { type: 'application/pdf' })

  const saveToDisk = (file: File) => {
    const href = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = href
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  }

  // ── App nativa ──
  const shareNative = async () => {
    setBusy('share')
    setError(null)
    try {
      const data = await blobToBase64(await fetchBlob())
      // Carga diferida: así el bundle web no arrastra los plugins nativos.
      const { Filesystem, Directory } = await import('@capacitor/filesystem')
      const { Share } = await import('@capacitor/share')
      // Caché: Android la limpia solo y NO exige permisos de almacenamiento.
      const { uri } = await Filesystem.writeFile({
        path: `${folio}.pdf`,
        data,
        directory: Directory.Cache,
      })
      await Share.share({ title: folio, text: `Comprobante de entrega ${folio}`, files: [uri] })
    } catch (e) {
      if (!isCancel(e)) setError('No se pudo compartir el PDF. Revisa tu conexión e intenta de nuevo.')
    } finally {
      setBusy(null)
    }
  }

  // ── Navegador ──
  const download = async () => {
    setBusy('download')
    setError(null)
    try {
      saveToDisk(await fetchPdf())
    } catch {
      setError('No se pudo descargar el PDF. Intenta de nuevo.')
    } finally {
      setBusy(null)
    }
  }

  const share = async () => {
    setBusy('share')
    setError(null)
    try {
      const file = await fetchPdf()
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: folio })
      } else {
        saveToDisk(file) // escritorio sin Web Share de archivos
      }
    } catch (e) {
      if (!isCancel(e)) setError('No se pudo compartir el PDF. Usa "Descargar".')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={isNative ? shareNative : share} disabled={busy !== null}>
          {busy === 'share' ? 'Preparando…' : '📤 Compartir PDF'}
        </Button>
        {!isNative && (
          <Button variant="secondary" onClick={download} disabled={busy !== null}>
            {busy === 'download' ? 'Descargando…' : '⬇ Descargar'}
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-center text-xs text-red-600">{error}</p>}
    </div>
  )
}
